/**
 * AIInterviewPage - Main orchestrator for AI Interview feature
 *
 * Tabs: Interview Plans (default) | Quick Interview (ad-hoc)
 */

import { useState, useCallback } from 'react'
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useAIInterview } from '@/hooks/useAIInterview'
import { useCreatePlan } from '@/hooks/useAIInterviewPlans'
import { AIInterviewSetup } from './AIInterviewSetup'
import { AIInterviewConversation } from './AIInterviewConversation'
import { AIInterviewReview } from './AIInterviewReview'
import { PlanListPage } from './plans/PlanListPage'
import { PlanEditorPage } from './plans/PlanEditorPage'
import { Button } from '@/components/ui/button'
import type { AIInterviewType } from '@ispot/shared'

interface AIInterviewPageProps {
  accountId: string
  projectId: string
}

export function AIInterviewPage({ accountId, projectId }: AIInterviewPageProps) {
  const [tab, setTab] = useState<'plans' | 'quick'>('plans')
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)

  // Plan creation
  const createPlan = useCreatePlan(accountId, projectId)

  // Quick interview state
  const interview = useAIInterview(accountId, projectId)

  const handleStart = useCallback(async (data: {
    researchGoal: string
    interviewType: AIInterviewType
    linkedPersonName: string
    focusTopics: string[]
  }) => {
    const session = await interview.createSession(data)
    if (session) {
      await interview.generatePlan(session.id)
    }
  }, [interview.createSession, interview.generatePlan])

  const handleRetryPlan = useCallback(async () => {
    if (interview.session) {
      await interview.generatePlan(interview.session.id)
    }
  }, [interview.session, interview.generatePlan])

  const handleSendMessage = useCallback((
    content: string,
    inputMethod?: 'text' | 'mcq',
    selectedMcqOption?: string,
  ) => {
    interview.sendMessage(content, inputMethod || 'text', selectedMcqOption)
  }, [interview.sendMessage])

  const handleComplete = useCallback(async () => {
    await interview.completeSession()
  }, [interview.completeSession])

  const handleNewInterview = useCallback(() => {
    window.location.reload()
  }, [])

  // If a plan is selected, show the plan editor
  if (selectedPlanId) {
    return (
      <PlanEditorPage
        accountId={accountId}
        projectId={projectId}
        planId={selectedPlanId}
        onBack={() => setSelectedPlanId(null)}
      />
    )
  }

  // If creating a new plan, also show the plan editor (with a fresh plan)
  // This is handled by the PlanListPage's onCreatePlan callback

  const renderQuickInterview = () => {
    if (!interview.session) {
      return (
        <AIInterviewSetup
          onStart={handleStart}
          onCancel={() => setTab('plans')}
          isLoading={interview.isLoading}
          error={interview.error}
        />
      )
    }

    if (interview.isPlanGenerating) {
      return (
        <div className="flex h-64 items-center justify-center">
          <div className="space-y-3 text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-accent-500" />
            <p className="text-body text-fg-muted">Generating interview plan...</p>
          </div>
        </div>
      )
    }

    if (interview.error && interview.messages.length === 0) {
      return (
        <div className="flex h-64 items-center justify-center">
          <div className="max-w-md space-y-4 text-center">
            <AlertCircle className="mx-auto h-8 w-8 text-danger-fg" />
            <div>
              <p className="text-body font-medium text-fg">Plan generation failed</p>
              <p className="mt-1 text-caption text-fg-muted">{interview.error}</p>
            </div>
            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" onClick={handleNewInterview} className="border-border">
                Start Over
              </Button>
              <Button size="sm" onClick={handleRetryPlan} className="bg-accent-500 text-bg-card hover:bg-accent-600">
                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      )
    }

    if (interview.session.status === 'completed') {
      return (
        <AIInterviewReview
          session={interview.session}
          messages={interview.messages}
          entities={interview.entities}
          onBack={() => window.history.back()}
          onNewInterview={handleNewInterview}
        />
      )
    }

    return (
      <AIInterviewConversation
        messages={interview.messages}
        entities={interview.entities}
        mcqOptions={interview.mcqOptions}
        plan={interview.plan}
        isStreaming={interview.isStreaming}
        personName={interview.session.linkedPersonName}
        onSendMessage={handleSendMessage}
        onComplete={handleComplete}
      />
    )
  }

  return (
    <div className="flex h-full flex-col bg-bg">
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'plans' | 'quick')} className="h-full flex flex-col">
        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5 max-md:flex-col">
          <div className="min-w-0">
            <h1 className="font-display text-heading font-semibold text-fg">Interviews</h1>
            <p className="mt-1 max-w-2xl text-body text-fg-secondary">
              Plan research interviews, share interview links, and run quick guided sessions from one workspace.
            </p>
          </div>
          <TabsList className="h-9 rounded-md border border-border bg-bg-secondary p-1">
            <TabsTrigger
              value="plans"
              className="h-7 rounded px-3 text-caption data-[state=active]:bg-bg-card data-[state=active]:text-fg data-[state=active]:shadow-none focus-visible:ring-accent-500"
            >
              Plans
            </TabsTrigger>
            <TabsTrigger
              value="quick"
              className="h-7 rounded px-3 text-caption data-[state=active]:bg-bg-card data-[state=active]:text-fg data-[state=active]:shadow-none focus-visible:ring-accent-500"
            >
              Quick Interview
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="plans" className="flex-1 overflow-auto mt-0">
          <PlanListPage
            accountId={accountId}
            projectId={projectId}
            onSelectPlan={(planId) => setSelectedPlanId(planId)}
            onCreatePlan={async () => {
              const plan = await createPlan.mutateAsync({
                title: 'New Interview Plan',
                researchGoal: 'Understand user needs and pain points',
              })
              if (plan?.id) setSelectedPlanId(plan.id)
            }}
          />
        </TabsContent>

        <TabsContent value="quick" className="flex-1 overflow-auto mt-0">
          {renderQuickInterview()}
        </TabsContent>
      </Tabs>
    </div>
  )
}
